import {
    CrowdHandlerWRState,
    CrowdHandlerWRSession
} from '../types/crowdhandler';

import { tokObfuscate } from './tokenObfuscator.js';
import { copyToken } from '../components/tokenHandler.js';
import type { I18nInstance } from '../i18n/index';


export const formatCrowdHandlerWRState = (session: CrowdHandlerWRSession, ch: CrowdHandlerWRState, i18n: I18nInstance) => {

    if (session && typeof session.status === 'number') {

        const { status, position } = session;

        // Use i18n for title/message — only override from customer pack if
        // they actually provide a translation for that key. The t() lookup
        // chain falls through to defaultStrings, which we don't want here
        // (the server-provided title/message should win over built-in defaults).
        const customerTitle = i18n.t('title');
        const customerMessage = i18n.t('message');

        // `session.priority` is the DB row id of the priority code record when a
        // code is in force, null/0 otherwise — NOT the code text. See the note
        // on CrowdHandlerWRSession.priority in types/crowdhandler.d.ts.
        //
        // An explicit null is a statement ("no code in force") and is honoured;
        // an absent field means "unknown", which leaves any existing verdict
        // intact. The template's JSON-LD first paint has no priority key at all,
        // so it must not be read as a denial.
        const priorityApplied = session.priority !== undefined
            ? Boolean(Number(session.priority))
            : null;

        const priorityState = {
            ...ch.priority,
            available: session.priorityAvailable,
            // `code` is the text the user typed and is bound to the input via
            // v-model — it is owned entirely by the client and must NEVER be
            // sourced from the response. Assigning session.priority here
            // rendered the row id inside the priority code box (a bare "3" in
            // the observed case) and re-asserted it on every poll.
            code: ch.priority?.code ?? null,
            applied: priorityApplied ?? ch.priority?.applied ?? null,
        };

        // Server confirms a code is in force → drive the flags the template
        // already gates its "Code applied!" block on. This is what covers a
        // code supplied via ?ch-code=, which never calls prioritySubmit and so
        // had no way to report success at all. Only ever sets these; the
        // negative case deliberately leaves them alone, because
        // makeSessionRequest's invalid-code branch owns the error state and the
        // retry response that follows it must not clear it.
        //
        // The `error` guard covers a subtler version of the same race: a user
        // who already holds priority and then submits a *second*, invalid code
        // gets a retry response still reporting the original priority id.
        // Without the guard that would overwrite the just-set rejection with
        // "Code applied!" and swallow the error for the code they just typed.
        // Typing in the box clears `error`, so the applied state comes back.
        if (priorityApplied === true && !ch.priority?.error) {
            priorityState.submitted = true;
            priorityState.success = true;
            priorityState.error = false;
        }

        // Only format session data - don't spread existing ch state
        // State merging happens in updateCrowdHandlerWRState
        let updated_session: Partial<CrowdHandlerWRState> = {
            title: customerTitle !== 'title' ? customerTitle : session.title,
            message: customerMessage !== 'message' ? customerMessage : session.message,
            status,
            position: position,
            promoted: session.promoted || null,
            urlRedirect: session.urlRedirect,
            onsale: session.onsale,
            slug: session.slug,
            logo: session.logo,
            stock: session.stock ?? null,
            responseID: session.responseID,
            captchaRequired: Boolean(session.captchaRequired),
            rate: session.rate,
            ttl: session.ttl,
            sessionsExpire: session.sessionsExpire,
            sessionsTimeout: session.sessionsTimeout,
            deployment: session.deployment,
            emailAvailable: session.emailAvailable,
            requested: session.requested,
            token: {
                value: session.token || null,
                obfuscated: tokObfuscate(session.token || ""),
                copied: false,
                copy: copyToken
            },
            priority: priorityState
        }

        if (session.wait) {
            updated_session.wait = session.wait;
        }

        if (session.estimate) {
            updated_session.estimate = session.estimate;
        }

        return updated_session;
    };

}
